import React, { useState, useEffect } from 'react';
import '../css/App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faEdit,
  faCopy,
  faPlus,
  faTrash,
  faSave,
  faTimes,
  faCheck,
  faInfoCircle,
  faList,
  faSlidersH,
  faEye,
  faCode,
  faStar,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import CookieConsentInterface from './CookieConsentInterface';
import cookieConsentConfig from './Cookieconsentconfig';

// ─── GoTrust Live Preview Widget ─────────────────────────────────────────────

const PREVIEW_TABS = ['Consent', 'Details', 'About'];

const DEFAULT_CATEGORIES = [
  {
    id: 'necessary',
    title: 'Strictly Necessary Cookies',
    description: 'Necessary cookies help make a website usable by enabling basic functions like page navigation and secure area access.',
    required: true,
    alwaysOn: true,
    defaultOn: true,
  },
  {
    id: 'preferences',
    title: 'Preferences',
    description: 'Preference cookies enable a website to remember information like your preferred language or region.',
    required: false,
    alwaysOn: false,
    defaultOn: false,
  },
  {
    id: 'statistics',
    title: 'Statistics & Analytics',
    description: 'Statistic cookies help website owners understand how visitors interact with websites by collecting information anonymously.',
    required: false,
    alwaysOn: false,
    defaultOn: false,
  },
  {
    id: 'marketing',
    title: 'Marketing & Targeting',
    description: 'Marketing cookies track visitors across websites to display relevant and engaging advertisements.',
    required: false,
    alwaysOn: false,
    defaultOn: true,
  },
];

const GoTrustLogoSVG = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 2L4 8v8c0 7 5.3 13.5 12 15.5C22.7 29.5 28 23 28 16V8L16 2z" fill="#2563eb" opacity="0.15" />
    <path d="M16 4L6 9.5v7c0 6 4.6 11.5 10 13.2C21.4 28 26 22.5 26 16.5v-7L16 4z" fill="#2563eb" opacity="0.3" />
    <path d="M21 13l-6.5 6.5L11 16" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function GoTrustPreview({ config, onAccept, onDecline, onPreferences, tenantName }) {
  const [activeTab, setActiveTab] = useState('Consent');
  const [selections, setSelections] = useState({});

  const categories = (config?.categories && Array.isArray(config.categories) && config.categories.length > 0)
    ? config.categories
    : DEFAULT_CATEGORIES;

  // Initialize selections whenever categories change
  useEffect(() => {
    setSelections(prev => {
      const next = { ...prev };
      categories.forEach(c => {
        if (c.required || c.alwaysOn) {
          next[c.id] = true;
        } else if (next[c.id] === undefined) {
          next[c.id] = !!c.defaultOn;
        }
      });
      return next;
    });
  }, [categories]);

  // ── Read all design config values ──
  const size = config?.size || 'medium';
  const buttonStyle = config?.buttonStyle || 'rounded';
  const backgroundColor = config?.backgroundColor || '#ffffff';
  const shade = config?.shade || 'light';
  const containerShape = config?.containerShape || 'rounded';

  // ── Size → maxWidth ──
  const sizeMap = {
    small: '360px',
    medium: '520px',
    large: '760px',
    fullscreen: '100%',
  };
  const maxWidth = sizeMap[size] || '520px';
  const isFullscreen = size === 'fullscreen';

  // ── Container shape → border-radius ──
  const shapeRadius =
    containerShape === 'rounded' ? '14px' :
      containerShape === 'rectangle' ? '6px' :
        '0px'; // square

  // ── Button style → border-radius ──
  const btnRadius =
    buttonStyle === 'pill' ? '999px' :
      buttonStyle === 'square' ? '4px' :
        '9px'; // rounded (default)

  const toggleCategory = (id) => {
    const cat = categories.find(c => c.id === id);
    if (cat && (cat.required || cat.alwaysOn)) return;
    setSelections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const primaryBtnStyle = {
    flex: 1,
    minWidth: '120px',
    padding: '11px 18px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: btnRadius,
    fontWeight: 700,
    fontSize: '0.92rem',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
    transition: 'background 0.15s, transform 0.1s',
  };

  const outlineBtnStyle = {
    flex: 1,
    minWidth: '120px',
    padding: '11px 18px',
    background: '#fff',
    color: '#2563eb',
    border: '1.5px solid #2563eb',
    borderRadius: btnRadius,
    fontWeight: 700,
    fontSize: '0.92rem',
    cursor: 'pointer',
    transition: 'background 0.15s',
  };

  const cardShadow =
    shade === 'dark' ? '0 12px 40px rgba(44,62,80,0.28)' :
      shade === 'medium' ? '0 6px 24px rgba(44,62,80,0.16)' :
        '0 4px 32px rgba(37,99,235,0.12)';

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: isFullscreen ? '0' : '12px 0',
      background: '#f1f5f9',
      borderRadius: '12px',
      minHeight: '440px',
      alignItems: 'center',
    }}>
      <div style={{
        width: '100%',
        maxWidth: isFullscreen ? '100%' : maxWidth,
        background: backgroundColor,
        borderRadius: isFullscreen ? '0' : shapeRadius,
        boxShadow: cardShadow,
        border: '1.5px solid #e2e8f0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px 14px',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GoTrustLogoSVG size={26} />
            <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e3a8a', letterSpacing: '-0.3px' }}>
              {config?.appName || tenantName || 'OneDPDP Cookie Consent'}
            </span>
          </div>
          <div style={{
            fontSize: '0.85rem',
            color: '#64748b',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            padding: '4px 10px',
            fontWeight: 600,
          }}>
            English
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', padding: '0 20px', borderBottom: '1px solid #e5e7eb' }}>
          {PREVIEW_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '3px solid #73767aff' : '3px solid transparent',
                padding: '12px 18px 10px',
                fontSize: '0.92rem',
                fontWeight: activeTab === tab ? 700 : 500,
                color: activeTab === tab ? '#1e3a8a' : '#6b7280',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {tab === 'Details' ? 'Preferences' : tab}
            </button>
          ))}
        </div>

        {/* Tab Body */}
        <div style={{ padding: '18px 24px', minHeight: '150px' }}>
          {activeTab === 'Consent' && (
            <div>
              <div
                dangerouslySetInnerHTML={{ __html: config?.content || 'We use cookies to optimize site functionality.' }}
                style={{ color: '#374151', fontSize: '0.92rem', lineHeight: '1.65' }}
              />
            </div>
          )}

          {activeTab === 'Details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {categories.map(cat => {
                const isAlwaysOn = cat.alwaysOn || cat.required;
                const isSelected = isAlwaysOn || !!selections[cat.id];
                return (
                  <div
                    key={cat.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      padding: '8px 0',
                      borderBottom: '1px solid #f3f4f6',
                    }}
                  >
                    <div style={{ flex: 1, paddingRight: '12px' }}>
                      <div style={{ color: '#1e3a8a', fontWeight: 700, fontSize: '0.88rem' }}>
                        {cat.title || cat.label || cat.id}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.76rem', marginTop: '3px', lineHeight: '1.4' }}>
                        {cat.description || ''}
                      </div>
                    </div>
                    {isAlwaysOn ? (
                      <span style={{
                        fontSize: '0.72rem',
                        color: '#059669',
                        background: '#d1fae5',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        Always On
                      </span>
                    ) : (
                      <div
                        onClick={() => toggleCategory(cat.id)}
                        style={{
                          width: '40px',
                          height: '22px',
                          borderRadius: '11px',
                          flexShrink: 0,
                          marginTop: '2px',
                          background: isSelected ? '#2563eb' : '#d1d5db',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: '3px',
                          left: isSelected ? '20px' : '3px',
                          width: '16px',
                          height: '16px',
                          background: '#fff',
                          borderRadius: '50%',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                          transition: 'left 0.2s',
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'About' && (
            <div>
              <h4 style={{ color: '#2563eb', fontWeight: 700, fontSize: '1rem', marginBottom: '8px' }}>
                {config?.aboutTitle || 'About this tool'}
              </h4>
              <p style={{ color: '#374151', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '10px' }}>
                {config?.aboutContent || 'This cookie consent tool is powered by Whitehats Technologies. It helps websites comply with privacy regulations like GDPR and CCPA by collecting informed consent from users before setting cookies.'}
              </p>
              {(config?.aboutLinkText || config?.aboutLinkUrl) && (
                <p style={{ color: '#374151', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  For more information, visit{' '}
                  <a
                    href={config?.aboutLinkUrl || 'https://www.whitehats.tech/'}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                  >
                    {config?.aboutLinkText || 'Whitehats Technologies'}
                  </a>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', padding: '0 24px 18px', flexWrap: 'wrap' }}>
          <button style={primaryBtnStyle} onClick={() => onAccept && onAccept('accepted')}>
            Allow all
          </button>
          <button style={outlineBtnStyle} onClick={() => onPreferences && onPreferences()}>
            Allow Selection
          </button>
          <button style={outlineBtnStyle} onClick={() => onDecline && onDecline('declined')}>
            Declined all
          </button>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '8px 24px 12px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '0.8rem',
          color: '#64748b',
        }}>
          <span>Powered by </span>
          <strong style={{ color: '#2563eb', marginLeft: '4px' }}>
            {config?.aboutLinkText || 'Whitehats'}
          </strong>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function CookieConsentPage({ tenantId, onSave, onBack }) {
  const [config, setConfig] = useState(cookieConsentConfig.getConfig());
  const [tenantName, setTenantName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sub-Navigation Tab for Control Panel: 'theme', 'message', 'categories', 'about'
  const [activeControlTab, setActiveControlTab] = useState('theme');

  // Multi-Banner Management State
  const [banners, setBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newBannerName, setNewBannerName] = useState('');
  const [newBannerDesc, setNewBannerDesc] = useState('');
  const [newBannerTemplate, setNewBannerTemplate] = useState('duplicate');
  const [creatingBanner, setCreatingBanner] = useState(false);
  const [viewMode, setViewMode] = useState('customizer'); // 'customizer' | 'all-banners'
  const [bannerSearch, setBannerSearch] = useState('');
  const [previewModalBanner, setPreviewModalBanner] = useState(null);
  const [bannerLimit, setBannerLimit] = useState(null);

  // Category Add / Edit State
  const [newCatTitle, setNewCatTitle] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [newCatRequired, setNewCatRequired] = useState(false);
  const [newCatDefault, setNewCatDefault] = useState(false);

  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatTitle, setEditCatTitle] = useState('');
  const [editCatDesc, setEditCatDesc] = useState('');
  const [editCatRequired, setEditCatRequired] = useState(false);
  const [editCatDefault, setEditCatDefault] = useState(false);

  const getResolvedTenantId = () => {
    return tenantId || config.tenantId || cookieConsentConfig.getTenantIdFromStorage();
  };

  const getApiBase = () => {
    let runtimeBase = '';
    try {
      runtimeBase = (window && window.__API_BASE__) ? window.__API_BASE__ : API_BASE || '';
    } catch (e) {
      runtimeBase = API_BASE || '';
    }
    return (runtimeBase || '').toString().replace(/\/$/, '');
  };

  const normalizeConfig = (rawCfg) => {
    const base = cookieConsentConfig.loadConfig();
    if (!rawCfg) return base;
    try {
      const parsed = typeof rawCfg === 'string' ? JSON.parse(rawCfg) : rawCfg;
      return {
        ...base,
        ...parsed,
        categories: (parsed && Array.isArray(parsed.categories) && parsed.categories.length > 0)
          ? parsed.categories
          : (base.categories || DEFAULT_CATEGORIES),
        aboutTitle: parsed?.aboutTitle || base.aboutTitle || 'About this tool',
        aboutContent: parsed?.aboutContent || base.aboutContent || '',
        aboutLinkText: parsed?.aboutLinkText || base.aboutLinkText || '',
        aboutLinkUrl: parsed?.aboutLinkUrl || base.aboutLinkUrl || '',
      };
    } catch (e) {
      return base;
    }
  };

  // Load Banners for this tenant from Database
  const loadTenantBanners = async () => {
    const tid = getResolvedTenantId();
    if (!tid) return;
    setLoading(true);
    setError('');
    try {
      const bannerRes = await cookieConsentConfig.fetchBannerData(tid, getApiBase());
      const list = bannerRes.data;
      if (bannerRes.limit !== null && bannerRes.limit !== undefined) {
        setBannerLimit(Number(bannerRes.limit));
      }
      if (Array.isArray(list) && list.length > 0) {
        setBanners(list);
        const def = list.find(b => b.is_default) || list[0];
        setActiveBanner(def);
        if (def && def.config) {
          const bannerCfg = normalizeConfig(def.config);
          setConfig(bannerCfg);
          cookieConsentConfig.saveConfig(bannerCfg);
        }
      } else {
        const fallbackBanner = {
          id: 'cookie_local_default',
          banner_id: 'cookie_banner_default',
          name: 'Main Cookie Banner',
          description: 'Default site-wide Cookie Consent banner',
          is_default: true,
          config: cookieConsentConfig.getConfig()
        };
        setBanners([fallbackBanner]);
        setActiveBanner(fallbackBanner);
      }
    } catch (err) {
      console.error('Failed to load cookie banners:', err);
      setError('Could not load cookie banners from server. Using local settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenantBanners();
  }, [tenantId]);

  // Load Tenant Name & Limit
  useEffect(() => {
    const tid = getResolvedTenantId();
    if (!tid) return;

    const url = (getApiBase() ? getApiBase() : '') + '/admin/tenants';
    let cancelled = false;
    axios.get(url)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.data) ? res.data.data : [];
        const found = list.find(t => Number(t.id) === Number(tid));
        setTenantName(found && found.name ? found.name : 'Tenant ' + tid);
        if (found) {
          const lim = found.cookie_banner_limit ?? found.limit ?? found.limits;
          if (lim !== undefined && lim !== null && lim !== '') {
            setBannerLimit(Number(lim));
          }
        }
      })
      .catch(() => {
        if (!cancelled) setTenantName('Tenant ' + tid);
      });

    return () => { cancelled = true; };
  }, [tenantId]);

  // Switch Banner
  const handleSelectBanner = (bannerIdVal) => {
    const found = banners.find(b => String(b.banner_id) === String(bannerIdVal) || String(b.id) === String(bannerIdVal));
    if (found) {
      setActiveBanner(found);
      const bannerCfg = normalizeConfig(found.config);
      setConfig(bannerCfg);
      cookieConsentConfig.saveConfig(bannerCfg);
    }
  };

  // Update Config locally
  const updateConfig = (field, value) => {
    setConfig(prev => {
      const next = { ...prev, [field]: value };
      cookieConsentConfig.saveConfig(next);
      return next;
    });
  };

  // ── Category Handlers (Add, Edit, Delete, Toggle Default) ──
  const handleAddCategory = () => {
    if (!newCatTitle.trim()) {
      alert('Please enter a preference/category title');
      return;
    }
    const cleanId = newCatTitle.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32) + '_' + Date.now().toString(36).slice(-4);
    const newCategory = {
      id: cleanId,
      title: newCatTitle.trim(),
      description: newCatDesc.trim(),
      required: !!newCatRequired,
      alwaysOn: !!newCatRequired,
      defaultOn: !!newCatDefault
    };

    const currentCategories = config.categories || DEFAULT_CATEGORIES;
    const updated = [...currentCategories, newCategory];
    updateConfig('categories', updated);
    setNewCatTitle('');
    setNewCatDesc('');
    setNewCatRequired(false);
    setNewCatDefault(false);
    setSuccessMessage('Category added successfully!');
    setSuccess(true);
    setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
  };

  const handleStartEditCategory = (cat) => {
    setEditingCatId(cat.id);
    setEditCatTitle(cat.title || cat.label || cat.id);
    setEditCatDesc(cat.description || '');
    setEditCatRequired(!!(cat.required || cat.alwaysOn));
    setEditCatDefault(!!cat.defaultOn);
  };

  const handleSaveEditCategory = () => {
    if (!editCatTitle.trim()) {
      alert('Category title cannot be empty');
      return;
    }
    const currentCategories = config.categories || DEFAULT_CATEGORIES;
    const updated = currentCategories.map(c => {
      if (c.id === editingCatId) {
        return {
          ...c,
          title: editCatTitle.trim(),
          description: editCatDesc.trim(),
          required: !!editCatRequired,
          alwaysOn: !!editCatRequired,
          defaultOn: !!editCatDefault
        };
      }
      return c;
    });
    updateConfig('categories', updated);
    setEditingCatId(null);
    setSuccessMessage('Category updated successfully!');
    setSuccess(true);
    setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
  };

  const handleDeleteCategory = (catId) => {
    if (!window.confirm('Are you sure you want to delete this cookie preference category?')) return;
    const currentCategories = config.categories || DEFAULT_CATEGORIES;
    if (currentCategories.length <= 1) {
      alert('At least one cookie category must remain.');
      return;
    }
    const updated = currentCategories.filter(c => c.id !== catId);
    updateConfig('categories', updated);
    setSuccessMessage('Category removed!');
    setSuccess(true);
    setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
  };

  const handleToggleCategoryDefault = (catId) => {
    const currentCategories = config.categories || DEFAULT_CATEGORIES;
    const updated = currentCategories.map(c => {
      if (c.id === catId) {
        return { ...c, defaultOn: !c.defaultOn };
      }
      return c;
    });
    updateConfig('categories', updated);
  };

  // Save active banner to Database
  const handleSaveToDatabase = async () => {
    setSaving(true);
    setError('');
    try {
      const tid = getResolvedTenantId();
      if (!activeBanner) {
        throw new Error('No cookie banner selected to save');
      }

      const payload = {
        name: activeBanner.name || 'Cookie Consent Banner',
        description: activeBanner.description || '',
        is_default: !!activeBanner.is_default,
        is_active: true,
        config: config,
        tenant_id: tid
      };

      const res = await cookieConsentConfig.saveBannerToDb(activeBanner.id || activeBanner.banner_id, payload, tid, getApiBase());
      if (res && res.success && res.data) {
        setBanners(prev => prev.map(b => (b.id === activeBanner.id ? res.data : b)));
        setActiveBanner(res.data);
      }

      setSuccessMessage('Cookie banner saved to database successfully!');
      setSuccess(true);
      if (onSave) onSave(config);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 3000);
    } catch (e) {
      console.error('Save cookie banner to DB failed:', e);
      setError('Failed to save to database. Check server connection.');
    } finally {
      setSaving(false);
    }
  };

  // Create New Banner in Database
  const handleCreateNewBanner = async () => {
    if (!newBannerName.trim()) {
      Swal.fire({ icon: 'info', title: 'Banner Name Required', text: 'Please enter a banner name' });
      return;
    }
    const tid = getResolvedTenantId();
    if (!tid) {
      Swal.fire({ icon: 'error', title: 'Tenant Error', text: 'Tenant ID could not be identified. Please ensure you are logged in.' });
      return;
    }
    if (bannerLimit && banners.length >= bannerLimit) {
      Swal.fire({
        icon: 'warning',
        title: 'Banner Limit Reached',
        text: `You have reached the maximum allowed limit of ${bannerLimit} Cookie Consent Banner(s) for your organization. Cannot create more banners.`,
        confirmButtonColor: '#2563eb'
      });
      return;
    }
    setCreatingBanner(true);
    try {
      const baseConfig = newBannerTemplate === 'duplicate' ? { ...config } : cookieConsentConfig.loadConfig();
      baseConfig.appName = tenantName || config.appName;

      const payload = {
        tenant_id: tid,
        name: newBannerName.trim(),
        description: newBannerDesc.trim(),
        is_default: banners.length === 0,
        is_active: true,
        config: baseConfig
      };

      const res = await cookieConsentConfig.createBannerInDb(payload, getApiBase());
      if (res && res.data) {
        const created = res.data;
        setBanners(prev => [...prev, created]);
        setActiveBanner(created);
        const parsed = normalizeConfig(created.config);
        setConfig(parsed);
        cookieConsentConfig.saveConfig(parsed);
        setShowNewModal(false);
        setNewBannerName('');
        setNewBannerDesc('');
        setSuccessMessage(`New cookie banner "${created.name}" created successfully!`);
        setSuccess(true);
        setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 3000);
      } else {
        throw new Error('Banner was not returned by server');
      }
    } catch (e) {
      console.error('Create cookie banner failed:', e);
      const errMsg = e.response?.data?.detail || e.message || 'Unknown error';
      Swal.fire({
        icon: 'error',
        title: 'Cannot Create Banner',
        text: errMsg,
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setCreatingBanner(false);
    }
  };

  // Set as Default (active banner or any selected banner from list)
  const handleSetDefault = async (targetBanner = activeBanner) => {
    const bannerToSet = targetBanner || activeBanner;
    if (!bannerToSet) return;
    try {
      const tid = getResolvedTenantId();
      await cookieConsentConfig.setDefaultBannerInDb(bannerToSet.id || bannerToSet.banner_id, tid, getApiBase());
      setBanners(prev => prev.map(b => ({
        ...b,
        is_default: (b.id === bannerToSet.id || b.banner_id === bannerToSet.banner_id)
      })));
      if (activeBanner?.id === bannerToSet.id || activeBanner?.banner_id === bannerToSet.banner_id) {
        setActiveBanner(prev => ({ ...prev, is_default: true }));
      } else {
        setActiveBanner(prev => prev ? ({ ...prev, is_default: false }) : prev);
      }
      setSuccessMessage(`"${bannerToSet.name}" is now the default cookie banner!`);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    } catch (e) {
      alert('Failed to set default cookie banner');
    }
  };

  // Delete Banner (active banner or any selected banner from list)
  const handleDeleteBanner = async (targetBanner = activeBanner) => {
    const bannerToDelete = targetBanner || activeBanner;
    if (!bannerToDelete) return;
    if (banners.length <= 1) {
      alert('Cannot delete the only cookie banner. At least one banner must remain.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${bannerToDelete.name}"?`)) return;

    try {
      const tid = getResolvedTenantId();
      await cookieConsentConfig.deleteBannerFromDb(bannerToDelete.id || bannerToDelete.banner_id, tid, getApiBase());
      const remaining = banners.filter(b => b.id !== bannerToDelete.id && b.banner_id !== bannerToDelete.banner_id);
      setBanners(remaining);
      if (activeBanner?.id === bannerToDelete.id || activeBanner?.banner_id === bannerToDelete.banner_id) {
        const nextActive = remaining[0];
        setActiveBanner(nextActive);
        const parsed = normalizeConfig(nextActive.config);
        setConfig(parsed);
        cookieConsentConfig.saveConfig(parsed);
      }
      if (previewModalBanner?.id === bannerToDelete.id || previewModalBanner?.banner_id === bannerToDelete.banner_id) {
        setPreviewModalBanner(null);
      }
      setSuccessMessage('Cookie banner deleted successfully!');
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    } catch (e) {
      alert('Failed to delete cookie banner');
    }
  };

  // Copy embed script for any banner
  const copyEmbedCodeForBanner = (b) => {
    if (!b) return;
    const bannerCfg = normalizeConfig(b.config);
    const code = cookieConsentConfig.getEmbedCode(b.banner_id, bannerCfg);
    navigator.clipboard.writeText(code).then(() => {
      setSuccessMessage(`Embed script for "${b.name}" copied to clipboard!`);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2500);
    });
  };

  // Select banner and switch to Customizer
  const handleEditBannerFromList = (b) => {
    if (!b) return;
    handleSelectBanner(b.banner_id);
    setViewMode('customizer');
  };

  // Simulate Send Consent Event
  const sendConsent = async (status) => {
    setError('');
    try {
      let consentStatus = typeof status === 'string' ? status : JSON.stringify(status);
      const payload = {
        tenant_id: getResolvedTenantId() || 0,
        banner_id: activeBanner?.banner_id || 'cookie_banner_default',
        consent_status: consentStatus,
        session_id: cookieConsentConfig.getSessionId(),
        source_url: window.location.href,
        app_name: config.appName || tenantName || window.location.hostname
      };

      const endpoint = (getApiBase() ? `${getApiBase()}` : '') + '/api/manager/cookie-consent';
      await axios.post(endpoint, payload);

      setSuccessMessage(status === 'accepted' ? 'Accepted successfully (Test event recorded)' : 'Declined successfully');
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
    } catch (e) {
      console.warn('Simulation consent post error:', e);
    }
  };

  const copyEmbedCode = () => {
    const code = cookieConsentConfig.getEmbedCode(activeBanner?.banner_id, config);
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="cookie-consent-page" style={{ padding: '0 0.5rem 2rem' }}>
      {/* Top Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', marginTop: '-0.25rem', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>Cookie Consent Management</h2>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {/* Tenant: <b>{tenantName || 'Tenant ' + (getResolvedTenantId() || '')}</b> • Total Banners: <b>{banners.length}{bannerLimit ? ` / ${bannerLimit}` : ''}</b> */}
            {bannerLimit && banners.length >= bannerLimit ? (
              <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}> (Limit Reached)</span>
            ) : null}
          </span>
        </div>

        {/* View Switcher: All Banners vs Customizer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              if (bannerLimit && banners.length >= bannerLimit) {
                Swal.fire({
                  icon: 'warning',
                  title: 'Banner Limit Reached',
                  text: `You have reached the maximum allowed limit of ${bannerLimit} Cookie Consent Banner(s) for your organization. You cannot create more banners.`,
                  confirmButtonColor: '#2563eb'
                });
                return;
              }
              setShowNewModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: bannerLimit && banners.length >= bannerLimit ? '#94a3b8' : '#059669',
              color: '#ffffff',
              border: 'none',
              padding: '7px 15px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.86rem',
              cursor: bannerLimit && banners.length >= bannerLimit ? 'not-allowed' : 'pointer',
              boxShadow: bannerLimit && banners.length >= bannerLimit ? 'none' : '0 2px 5px rgba(5,150,105,0.25)',
              transition: 'all 0.15s ease'
            }}
            title={bannerLimit && banners.length >= bannerLimit ? 'Banner limit reached' : 'Create a new banner for this tenant'}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create New Banner</span>
          </button>

          <div style={{
            display: 'inline-flex',
            background: '#f1f5f9',
            padding: '3px',
            borderRadius: '9px',
            border: '1px solid #cbd5e1'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('all-banners')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'all-banners' ? '#2563eb' : 'transparent',
                color: viewMode === 'all-banners' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'all-banners' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faList} />
              <span>All Banners ({banners.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('customizer')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 14px',
                borderRadius: '7px',
                border: 'none',
                fontSize: '0.86rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'customizer' ? '#2563eb' : 'transparent',
                color: viewMode === 'customizer' ? '#ffffff' : '#475569',
                boxShadow: viewMode === 'customizer' ? '0 1px 3px rgba(37,99,235,0.3)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              <FontAwesomeIcon icon={faSlidersH} />
              <span>Banner Customizer</span>
            </button>
          </div>

          <button
            className="back-btn"
            onClick={() => {
              if (typeof onBack === 'function') onBack();
              else window.history.back();
            }}
          >
            Back
          </button>
        </div>
      </div>

      {/* Notifications */}
      {success && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#15803d', padding: '10px 16px', borderRadius: 8, marginBottom: '1rem', fontWeight: 600 }}>
          ✓ {successMessage}
        </div>
      )}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: '1rem', fontWeight: 600 }}>
          ⚠ {error}
        </div>
      )}

      {/* Main Content: All Banners Table View OR Customizer */}
      {viewMode === 'all-banners' ? (
        /* ═════════════════════════════════════════════════════════════════════
           ALL BANNERS MANAGEMENT TABLE & OVERVIEW (See all banners together)
           ═════════════════════════════════════════════════════════════════════ */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Table Container Card */}
          <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            {/* Search and Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ position: 'relative', minWidth: '280px', flex: 1, maxWidth: '420px' }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem' }} />
                <input
                  type="text"
                  placeholder="Search banners by name, banner ID, app..."
                  value={bannerSearch}
                  onChange={e => setBannerSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    borderRadius: 8,
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.88rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                Showing <b>{banners.filter(b => {
                  if (!bannerSearch.trim()) return true;
                  const q = bannerSearch.toLowerCase();
                  return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                }).length}</b> of <b>{banners.length}</b> cookie banners
              </div>
            </div>

            {/* Banners Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>BANNER DETAILS</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>TARGET APP / SITE</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>APPEARANCE & PREFERENCES</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem' }}>STATUS</th>
                    <th style={{ padding: '12px 14px', color: '#475569', fontWeight: 700, fontSize: '0.82rem', textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {banners
                    .filter(b => {
                      if (!bannerSearch.trim()) return true;
                      const q = bannerSearch.toLowerCase();
                      return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                    })
                    .map((b, idx) => {
                      const bCfg = normalizeConfig(b.config);
                      const isCurrent = activeBanner?.id === b.id || activeBanner?.banner_id === b.banner_id;
                      const catCount = (bCfg.categories && Array.isArray(bCfg.categories)) ? bCfg.categories.length : 0;

                      return (
                        <tr
                          key={b.banner_id || b.id || idx}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isCurrent ? '#f0f9ff' : 'transparent',
                            transition: 'background 0.15s'
                          }}
                        >
                          {/* Banner Details */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.94rem' }}>
                                {b.name}
                              </span>
                              {b.is_default && (
                                <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 5, fontSize: '0.72rem', fontWeight: 700 }}>
                                  ★ Default
                                </span>
                              )}
                              {isCurrent && (
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 5, fontSize: '0.72rem', fontWeight: 700 }}>
                                  Selected
                                </span>
                              )}
                            </div>
                            {b.description && (
                              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 3 }}>
                                {b.description}
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: 5 }}>
                              <code style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                                {b.banner_id}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(b.banner_id);
                                  setSuccessMessage(`Banner ID "${b.banner_id}" copied!`);
                                  setSuccess(true);
                                  setTimeout(() => { setSuccess(false); setSuccessMessage(''); }, 2000);
                                }}
                                title="Copy Banner ID"
                                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2, fontSize: '0.75rem' }}
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            </div>
                          </td>

                          {/* Target App */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.88rem' }}>
                              {bCfg.appName || tenantName || 'Default Website'}
                            </div>
                            {/* <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
                              Tenant ID: {b.tenant_id}
                            </div> */}
                          </td>

                          {/* Appearance & Preferences */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {/* <span style={{
                                width: 14,
                                height: 14,
                                // borderRadius: '50%',
                                background: bCfg.backgroundColor || '#ffffff',
                                border: '1px solid #cbd5e1',
                                display: 'inline-block'
                              }} title={`Background: ${bCfg.backgroundColor || '#ffffff'}`} /> */}
                              <span style={{ fontSize: '0.8rem', color: '#475569', textTransform: 'capitalize' }}>
                                {bCfg.buttonStyle || 'rounded'} • {bCfg.placement || 'bottom'} • {bCfg.size || 'medium'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.76rem', color: '#059669', fontWeight: 600, marginTop: 4 }}>
                              ✓ {catCount} preference categories configured
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: '14px', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '0.76rem',
                                color: '#16a34a',
                                fontWeight: 700
                              }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }}></span>
                                Active
                              </span>
                              {b.is_default ? (
                                <span style={{ fontSize: '0.74rem', color: '#b45309', fontWeight: 600 }}>
                                  Default Site Banner
                                </span>
                              ) : (
                                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                                  Secondary Banner
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '14px', verticalAlign: 'top', textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexWrap: 'wrap' }}>
                              {/* Customize Button */}
                              <button
                                type="button"
                                onClick={() => handleEditBannerFromList(b)}
                                style={{
                                  background: '#2563eb',
                                  color: '#fff',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Open this banner in Customizer"
                              >
                                <FontAwesomeIcon icon={faEdit} /> Customize
                              </button>

                              {/* Preview Button */}
                              <button
                                type="button"
                                onClick={() => setPreviewModalBanner(b)}
                                style={{
                                  background: '#f0fdf4',
                                  border: '1px solid #bbf7d0',
                                  color: '#15803d',
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Quick Preview this banner"
                              >
                                <FontAwesomeIcon icon={faEye} /> Preview
                              </button>

                              {/* Copy Embed Code */}
                              <button
                                type="button"
                                onClick={() => copyEmbedCodeForBanner(b)}
                                style={{
                                  background: '#f8fafc',
                                  border: '1px solid #cbd5e1',
                                  color: '#334155',
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px'
                                }}
                                title="Copy HTML embed script for this banner"
                              >
                                <FontAwesomeIcon icon={faCode} /> Embed Code
                              </button>

                              {/* Set as Default (if not default) */}
                              {!b.is_default && (
                                <button
                                  type="button"
                                  onClick={() => handleSetDefault(b)}
                                  style={{
                                    background: '#fefce8',
                                    border: '1px solid #fde047',
                                    color: '#854d0e',
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  title="Make this the default banner for the website"
                                >
                                  ★ Make Default
                                </button>
                              )}

                              {/* Delete Banner */}
                              {banners.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBanner(b)}
                                  style={{
                                    background: '#fee2e2',
                                    border: '1px solid #fecaca',
                                    color: '#b91c1c',
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    fontSize: '0.76rem',
                                    cursor: 'pointer'
                                  }}
                                  title="Delete this banner"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {banners.filter(b => {
                if (!bannerSearch.trim()) return true;
                const q = bannerSearch.toLowerCase();
                return (b.name || '').toLowerCase().includes(q) || (b.banner_id || '').toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
              }).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                    <p style={{ margin: 0, fontSize: '0.95rem' }}>No cookie banners match "<b>{bannerSearch}</b>"</p>
                    <button
                      type="button"
                      onClick={() => setBannerSearch('')}
                      style={{ marginTop: 10, background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem' }}
                    >
                      Clear Search
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      ) : (
        /* ═════════════════════════════════════════════════════════════════════
           CUSTOMIZER VIEW (Interactive 3-Column Studio)
           ═════════════════════════════════════════════════════════════════════ */
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(330px, 390px) minmax(360px, 1fr) minmax(280px, 320px)', gap: '1.25rem' }}>

          {/* Column 1: Configuration Controls Panel with Sub-Tabs */}
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Sub Navigation Tabs */}
            <div style={{ display: 'flex', gap: '4px', borderBottom: '1.5px solid #e2e8f0', paddingBottom: '8px', overflowX: 'auto' }}>
              {[
                { id: 'theme', label: 'Theme & Style' },
                { id: 'message', label: 'Message' },
                { id: 'categories', label: 'Preferences' },
                { id: 'about', label: 'About Us' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveControlTab(tab.id)}
                  style={{
                    background: activeControlTab === tab.id ? '#2563eb' : 'transparent',
                    color: activeControlTab === tab.id ? '#fff' : '#64748b',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB 1: Theme & Style */}
            {activeControlTab === 'theme' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="cookie-design-row">
                  <label>Banner Name (Admin):</label>
                  <input
                    type="text"
                    value={activeBanner?.name || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setActiveBanner(prev => ({ ...prev, name: val }));
                    }}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="cookie-design-row">
                  <label>App / Website Name:</label>
                  <input
                    type="text"
                    value={config.appName || ''}
                    onChange={e => updateConfig('appName', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="cookie-design-row">
                  <label>Button Style:</label>
                  <select
                    value={config.buttonStyle || 'rounded'}
                    onChange={e => updateConfig('buttonStyle', e.target.value)}
                  >
                    <option value="rounded">Rounded</option>
                    <option value="square">Square</option>
                    <option value="pill">Pill</option>
                  </select>
                </div>

                <div className="cookie-design-row">
                  <label>Placement:</label>
                  <select
                    value={config.placement || 'bottom'}
                    onChange={e => updateConfig('placement', e.target.value)}
                  >
                    <option value="bottom">Bottom (default)</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="full">Full Page</option>
                    <option value="on-login">On Login</option>
                  </select>
                </div>

                <div className="cookie-design-row">
                  <label>Background Color:</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                    <input
                      type="color"
                      value={config.backgroundColor || '#ffffff'}
                      onChange={e => updateConfig('backgroundColor', e.target.value)}
                      style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', background: 'transparent' }}
                    />
                    <input
                      type="text"
                      value={config.backgroundColor || '#ffffff'}
                      onChange={e => updateConfig('backgroundColor', e.target.value)}
                      style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                    />
                  </div>
                </div>

                <div className="cookie-design-row">
                  <label>Shade / Shadow:</label>
                  <select
                    value={config.shade || 'light'}
                    onChange={e => updateConfig('shade', e.target.value)}
                  >
                    <option value="light">Light</option>
                    <option value="medium">Medium</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>

                <div className="cookie-design-row">
                  <label>Container Shape:</label>
                  <select
                    value={config.containerShape || 'rounded'}
                    onChange={e => updateConfig('containerShape', e.target.value)}
                  >
                    <option value="rounded">Rounded Corners</option>
                    <option value="square">Square</option>
                    <option value="rectangle">Rectangle</option>
                  </select>
                </div>

                <div className="cookie-design-row">
                  <label>Container Size:</label>
                  <select
                    value={config.size || 'medium'}
                    onChange={e => updateConfig('size', e.target.value)}
                  >
                    <option value="small">Small (360px)</option>
                    <option value="medium">Medium (520px)</option>
                    <option value="large">Large (760px)</option>
                    <option value="fullscreen">Full Screen</option>
                  </select>
                </div>
              </div>
            )}

            {/* TAB 2: Widget Message */}
            {activeControlTab === 'message' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Primary consent notice message shown to website visitors in the banner.
                </div>
                <ReactQuill
                  value={config.content || ''}
                  onChange={(value) => updateConfig('content', value)}
                  theme="snow"
                  style={{ height: 160, marginBottom: 50 }}
                />
              </div>
            )}

            {/* TAB 3: Preferences / Cookie Categories */}
            {activeControlTab === 'categories' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Manage individual cookie categories that visitors can toggle in the <b>Preferences</b> tab.
                </div>

                {/* Existing Categories List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                  {(config.categories || DEFAULT_CATEGORIES).map((cat) => {
                    const isAlwaysOn = cat.alwaysOn || cat.required;
                    const isEditing = editingCatId === cat.id;

                    return (
                      <div
                        key={cat.id}
                        style={{
                          border: isEditing ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: '10px',
                          background: isEditing ? '#eff6ff' : '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}
                      >
                        {isEditing ? (
                          // Inline Edit Form
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <input
                              type="text"
                              placeholder="Category Title"
                              value={editCatTitle}
                              onChange={e => setEditCatTitle(e.target.value)}
                              style={{ padding: '6px 8px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                            <textarea
                              rows={2}
                              placeholder="Description"
                              value={editCatDesc}
                              onChange={e => setEditCatDesc(e.target.value)}
                              style={{ padding: '6px 8px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <label style={{ fontSize: '0.78rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="checkbox"
                                  checked={editCatRequired}
                                  onChange={e => setEditCatRequired(e.target.checked)}
                                />
                                Always On / Required
                              </label>
                              <label style={{ fontSize: '0.78rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="checkbox"
                                  checked={editCatDefault}
                                  disabled={editCatRequired}
                                  onChange={e => setEditCatDefault(e.target.checked)}
                                />
                                Default ON
                              </label>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '4px' }}>
                              <button
                                type="button"
                                onClick={() => setEditingCatId(null)}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.76rem', cursor: 'pointer' }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveEditCategory}
                                style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.76rem', cursor: 'pointer' }}
                              >
                                <FontAwesomeIcon icon={faCheck} /> Save Category
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Display Mode
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, paddingRight: '8px' }}>
                              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                                {cat.title || cat.label || cat.id}
                              </div>
                              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '2px', lineHeight: '1.4' }}>
                                {cat.description || 'No description'}
                              </div>
                              <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                {isAlwaysOn ? (
                                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '2px 6px', borderRadius: 4 }}>
                                    Always On (Mandatory)
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '0.68rem', fontWeight: 600, color: cat.defaultOn ? '#2563eb' : '#64748b', background: cat.defaultOn ? '#dbeafe' : '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                                    Default: {cat.defaultOn ? 'ON' : 'OFF'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => handleStartEditCategory(cat)}
                                style={{
                                  background: '#f8fafc',
                                  border: '1px solid #cbd5e1',
                                  padding: '4px 8px',
                                  borderRadius: 5,
                                  fontSize: '0.72rem',
                                  color: '#2563eb',
                                  cursor: 'pointer'
                                }}
                                title="Edit Preference"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              {!isAlwaysOn && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleCategoryDefault(cat.id)}
                                  style={{
                                    background: cat.defaultOn ? '#10b981' : '#e2e8f0',
                                    color: cat.defaultOn ? '#fff' : '#475569',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: 5,
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  title="Toggle default state"
                                >
                                  {cat.defaultOn ? 'ON' : 'OFF'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteCategory(cat.id)}
                                style={{
                                  background: '#fee2e2',
                                  border: '1px solid #fecaca',
                                  color: '#dc2626',
                                  padding: '4px 8px',
                                  borderRadius: 5,
                                  fontSize: '0.72rem',
                                  cursor: 'pointer'
                                }}
                                title="Delete Preference"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add New Category Form */}
                <div style={{ borderTop: '1.5px solid #e2e8f0', paddingTop: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '8px', color: '#1e293b' }}>
                    + Add Preference Category
                  </div>
                  <input
                    type="text"
                    placeholder="Category Name (e.g. Social Media Cookies)"
                    value={newCatTitle}
                    onChange={e => setNewCatTitle(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px', fontSize: '0.82rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <textarea
                    rows={2}
                    placeholder="Category Description"
                    value={newCatDesc}
                    onChange={e => setNewCatDesc(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px', fontSize: '0.8rem', borderRadius: 6, border: '1px solid #cbd5e1', marginBottom: 6 }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontSize: '0.78rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="checkbox"
                        checked={newCatRequired}
                        onChange={e => setNewCatRequired(e.target.checked)}
                      />
                      Always On / Required
                    </label>
                    <label style={{ fontSize: '0.78rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="checkbox"
                        checked={newCatDefault}
                        disabled={newCatRequired}
                        onChange={e => setNewCatDefault(e.target.checked)}
                      />
                      Default Enabled (ON)
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: '#059669',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(5,150,105,0.2)'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Preference Category
                  </button>
                </div>
              </div>
            )}

            {/* TAB 4: About Us */}
            {activeControlTab === 'about' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Customize the <b>About</b> tab details displayed in the cookie consent widget.
                </div>

                <div className="cookie-design-row">
                  <label>Section Title:</label>
                  <input
                    type="text"
                    placeholder="e.g. About this tool or Privacy Statement"
                    value={config.aboutTitle || ''}
                    onChange={e => updateConfig('aboutTitle', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="cookie-design-row">
                  <label>About Text / Description:</label>
                  <textarea
                    rows={4}
                    placeholder="Explain why and how your site collects cookies under GDPR/CCPA..."
                    value={config.aboutContent || ''}
                    onChange={e => updateConfig('aboutContent', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                  />
                </div>

                <div className="cookie-design-row">
                  <label>Company Link Text:</label>
                  <input
                    type="text"
                    placeholder="e.g. Whitehats Technologies"
                    value={config.aboutLinkText || ''}
                    onChange={e => updateConfig('aboutLinkText', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="cookie-design-row">
                  <label>Company Link URL:</label>
                  <input
                    type="text"
                    placeholder="https://www.whitehats.tech/"
                    value={config.aboutLinkUrl || ''}
                    onChange={e => updateConfig('aboutLinkUrl', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>
            )}

            {/* Save to Database Button */}
            <button
              className="tour-target-cookie-save"
              onClick={handleSaveToDatabase}
              disabled={saving}
              style={{
                marginTop: '0.5rem',
                width: '100%',
                padding: '12px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 2px 6px rgba(37,99,235,0.25)'
              }}
            >
              <FontAwesomeIcon icon={faSave} />
              {saving ? 'Saving to Database...' : 'Save Banner to Database'}
            </button>
          </div>

          {/* Column 2: Live Preview */}
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Live Preview</h3>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Testing {activeBanner?.name || 'Default Banner'}
              </span>
            </div>
            <GoTrustPreview
              config={config}
              onAccept={sendConsent}
              onDecline={sendConsent}
              onPreferences={() => setPrefsOpen(true)}
              tenantName={tenantName}
            />
          </div>

          {/* Column 3: Embed Code */}
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', color: '#0f172a' }}>CDN Embed Code</h4>
            <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginBottom: '8px' }}>
              Banner ID: <b>{activeBanner?.banner_id || 'cookie_banner_default'}</b>
            </span>
            <textarea
              className="cookie-embed-code-textarea"
              readOnly
              value={cookieConsentConfig.getEmbedCode(activeBanner?.banner_id, config)}
              style={{
                width: '100%',
                minHeight: '220px',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                borderRadius: '8px',
                background: '#f8fafc',
                color: '#2a3a5e',
                padding: '0.75rem',
                resize: 'none',
                border: '1.5px solid #e3eafc',
                lineHeight: '1.4'
              }}
            />
            <button
              onClick={copyEmbedCode}
              style={{
                marginTop: '0.75rem',
                width: '100%',
                padding: '10px',
                background: copied ? '#10b981' : '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faCopy} /> {copied ? 'Copied!' : 'Copy Script Tag'}
            </button>
            <p style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '10px', lineHeight: '1.4' }}>
              Paste this tag into your website's <code>&lt;head&gt;</code> or bottom of <code>&lt;body&gt;</code>. Any changes in preferences or About Us update live without re-pasting!
            </p>
          </div>
        </div>
      )}

      {/* Quick Preview Modal for Any Banner */}
      {previewModalBanner && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            maxWidth: 720,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
            border: '1.5px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>
                  Preview: {previewModalBanner.name}
                </h3>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  Banner ID: <code>{previewModalBanner.banner_id}</code> {previewModalBanner.is_default ? '★ (Default Banner)' : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModalBanner(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <GoTrustPreview
              config={normalizeConfig(previewModalBanner.config)}
              tenantName={tenantName}
              onAccept={() => { }}
              onDecline={() => { }}
              onPreferences={() => { }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
              <button
                type="button"
                onClick={() => copyEmbedCodeForBanner(previewModalBanner)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#334155',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faCode} /> Copy Embed Code
              </button>
              <button
                type="button"
                onClick={() => {
                  handleEditBannerFromList(previewModalBanner);
                  setPreviewModalBanner(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 7,
                  border: 'none',
                  background: '#2563eb',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Open in Customizer
              </button>
              <button
                type="button"
                onClick={() => setPreviewModalBanner(null)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 7,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#64748b',
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create New Banner */}
      {showNewModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.5)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: 16
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            maxWidth: 480,
            width: '100%',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            border: '1.5px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', color: '#0f172a' }}>
              Create New Cookie Banner
            </h3>
            <p style={{ margin: '0 0 18px 0', fontSize: '0.85rem', color: '#64748b' }}>
              Create a distinct cookie consent banner with its own styles, preferences, and embed script.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: 6 }}>
                Banner Name *
              </label>
              <input
                type="text"
                placeholder="e.g. EU Region Cookie Banner, Marketing Campaign..."
                value={newBannerName}
                onChange={e => setNewBannerName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: 6 }}>
                Description (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Used for European visitors on subdomains"
                value={newBannerDesc}
                onChange={e => setNewBannerDesc(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: 6 }}>
                Initial Template
              </label>
              <select
                value={newBannerTemplate}
                onChange={e => setNewBannerTemplate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 8,
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.9rem',
                  background: '#f8fafc'
                }}
              >
                <option value="duplicate">Copy Current Cookie Banner</option>
                <option value="blank">Clean Standard Default</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNewModal(false)}
                disabled={creatingBanner}
                style={{
                  padding: '9px 16px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewBanner}
                disabled={creatingBanner}
                style={{
                  padding: '9px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#059669',
                  color: '#ffffff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(5,150,105,0.25)'
                }}
              >
                {creatingBanner ? 'Creating...' : 'Create Banner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {prefsOpen && (
        <CookieConsentInterface
          initial={config.preferences}
          buttonStyle={config.buttonStyle}
          placement={config.placement}
          backgroundColor={config.backgroundColor}
          shade={config.shade}
          containerShape={config.containerShape}
          onConfirm={async (prefs) => {
            cookieConsentConfig.updatePreferences(prefs);
            await sendConsent(prefs);
            setPrefsOpen(false);
          }}
          onClose={() => setPrefsOpen(false)}
        />
      )}
    </div>
  );
}

export default CookieConsentPage;